import React, { useState, useEffect } from 'react';
import { updateGemeente, getFluviusPrive, verversBevolkingscijfers, verversBevolkingGemeente,
         getBevolking, getVlootverhouding, getIngelogdeGebruiker } from '../api';
import { evAandeelGemeente, FLUVIUS_KAPPA, vlootSignaal, EV_AANDEEL_VLAANDEREN } from '../gemeenteData';
import GisNetbeheerder from './GisNetbeheerder';

// Palet gelijk aan Dashboard.js en AppWithOnboarding.js
const N = {
  ink:'#12161C', ink2:'#3D4652', ink3:'#6B7684', ink4:'#9AA4B1',
  paper:'#FFFFFF', paper2:'#FBFAF7', canvas:'#ECEAE5',
  line:'#D9D5CD', line2:'#C4BFB5', head:'#F6F4F0',
  accent:'#1F5C6B', accent2:'#2D7F92',
  ok:'#3F7D4E', warn:'#9C6420', warnBg:'#F9F1E4', warnBd:'#E8D3AC',
  red:'#B4442F',
};

// Zelfde drie wijktypes als het gevalideerde rekenmodel (zie Leeswijzer §5).
// Een wijk kan er meerdere aanvinken (bijvoorbeeld woonwijk + bedrijventerrein
// voor een hybride wijk); calcWijk middelt dan de bijbehorende doelgroepenmix.
const WIJKTYPES = [
  { key:'binnenstad',       label:'Binnenstad' },
  { key:'woonwijk',         label:'Woonwijk' },
  { key:'bedrijventerrein', label:'Bedrijventerrein' },
];

// Duizendtallen met een punt, en een verhouding met een komma en een decimaal.
function heelGetal(n) {
  return Math.round(Number(n) || 0).toLocaleString('nl-BE');
}
function kommagetal(n) {
  return (Math.round((Number(n) || 0) * 10) / 10).toFixed(1).replace('.', ',');
}

export default function GemeenteEditor({ gemeente, onSave, onClose, capex, onCapex, onGisGewijzigd }) {
  const [naam,       setNaam]       = useState(gemeente.naam);
  const [inwoners,   setInwoners]   = useState(gemeente.inwoners);
  const [voertuigen, setVoertuigen] = useState(gemeente.voertuigen);
  const [privePctBerekend, setPrivePctBerekend] = useState(Math.round((gemeente.privePctBerekend ?? 0.5) * 100));
  const [evOverride2030, setEvOverride2030] = useState(gemeente.evAandeelOverride?.[2030] != null ? Math.round(gemeente.evAandeelOverride[2030]*1000)/10 : '');
  const [evOverride2035, setEvOverride2035] = useState(gemeente.evAandeelOverride?.[2035] != null ? Math.round(gemeente.evAandeelOverride[2035]*1000)/10 : '');
  const [postcodes, setPostcodes] = useState((gemeente.postcodes || []).join(', '));
  // Wordt hier NIET getoond: het invoerveld staat op tabblad Plaatsing, naast
  // het gevolg dat het heeft. De waarde wordt hier wel vastgehouden en weer
  // meegestuurd, anders zou opslaan vanuit dit scherm hem wissen.
  const [pvpNormPct, setPvpNormPct] = useState(gemeente.pvpNormPct != null ? gemeente.pvpNormPct : '');
  const [fluviusLoading, setFluviusLoading] = useState(false);
  const [fluviusResultaat, setFluviusResultaat] = useState(null);
  const [fluviusError, setFluviusError] = useState('');
  // Elke wijk staat standaard aan. Uitschakelen werkt hier hetzelfde als tijdens
  // het onboarden: de wijk wordt bij het opslaan niet meegestuurd en verdwijnt
  // daarmee uit de gemeente.
  const [wijken,     setWijken]     = useState(
    (gemeente.wijken || []).map(w => ({ ...w, wijktype: w.wijktype || ['woonwijk'], actief: w.actief !== false }))
  );
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');

  const [activeTab,  setActiveTab]  = useState('gemeente');
  // De bedragen per laadpunt horen bij de instellingen van de gemeente, niet
  // bij haar kerngegevens. Ze komen daarom als prop binnen en gaan bij het
  // opslaan met onCapex weer terug; de rest van dit venster raakt ze niet aan.
  const [capexAC,  setCapexAC]  = useState(capex?.AC  ?? 4500);
  const [capexDC,  setCapexDC]  = useState(capex?.DC  ?? 29000);
  const [capexHPC, setCapexHPC] = useState(capex?.HPC ?? 82000);

  // De twee tellingen van het wagenpark van deze gemeente, opgehaald bij het
  // openen van het venster. Ze bepalen of er boven de EV-velden een melding
  // komt te staan. Mislukt het ophalen, dan blijft die melding weg en gaat de
  // rest van het venster gewoon door; de reden staat in de console.
  const [vloot, setVloot] = useState(null);
  useEffect(() => {
    let geldig = true;
    getVlootverhouding(gemeente.id)
      .then(r => { if (geldig) setVloot(r); })
      .catch(e => console.warn('Vlootverhouding niet opgehaald:', e.message));
    return () => { geldig = false; };
  }, [gemeente.id]);
  const vlootMelding = vloot ? vlootSignaal(vloot.inschrijvingen, vloot.huishoudautos) : null;
  const vlootOnbekend = Boolean(vloot) && (vloot.inschrijvingen == null || vloot.huishoudautos == null);
  // Is het aantal auto's uit het huishoudbestand berekend, dan hoort niemand
  // het hier meer te kunnen overschrijven.
  const berekendUitBron = Boolean(vloot) && vloot.huishoudautos != null;

  const HUIDIG_JAAR = 2026; // Fluvius-registraties zijn een momentopname van nu, geen toekomstprognose

  const verversVanuitFluvius = async () => {
    setFluviusLoading(true); setFluviusError(''); setFluviusResultaat(null);
    try {
      const r = await getFluviusPrive(gemeente.id);
      const evPct = evAandeelGemeente(HUIDIG_JAAR);
      const huidigeEvs = (parseInt(voertuigen) || 0) * evPct;
      const voorgesteld = huidigeEvs > 0 ? Math.min(1, r.totaalPrivePunten / huidigeEvs) : null;
      setFluviusResultaat({ ...r, huidigeEvs, voorgesteld });
    } catch(e) {
      setFluviusError(e.message);
    }
    setFluviusLoading(false);
  };

  // Auto-load Fluvius bij openen van editor als postcodes bekend zijn.
  // Zo staat het percentage direct gevuld en hoeft de gebruiker de knop alleen
  // te gebruiken om te verversen. Vuurt exact één keer bij mount af.
  useEffect(() => {
    if (gemeente.postcodes && gemeente.postcodes.length > 0) {
      verversVanuitFluvius();
    }
  }, []);

  const [bevolkingLoading, setBevolkingLoading] = useState(false);
  const [bevolkingResultaat, setBevolkingResultaat] = useState(null);
  const [bevolkingError, setBevolkingError] = useState('');

  // De beheerder ververst alle gemeenten tegelijk, zoals voorheen. Iedereen
  // anders alleen deze gemeente: besluit van 15 oktober 2026.
  const isBeheerder = getIngelogdeGebruiker()?.rol === 'beheerder';

  const verversBevolking = async () => {
    setBevolkingLoading(true); setBevolkingError(''); setBevolkingResultaat(null);
    if (!isBeheerder) {
      try {
        const r = await verversBevolkingGemeente(gemeente.id);
        setInwoners(String(r.nieuw));
        setBevolkingResultaat({ eigen: true, ...r });
        // Het getal staat al in de database. Ook in de rest van het scherm
        // bijwerken, zodat opslaan of later openen het oude getal niet
        // terugschrijft.
        if (onSave) onSave({ ...gemeente, inwoners: r.nieuw });
      } catch (e) {
        setBevolkingError(e.message);
      }
      setBevolkingLoading(false);
      return;
    }
    try {
      const r = await verversBevolkingscijfers();
      setBevolkingResultaat(r);
      try {
        const eigen = await getBevolking(naam);
        if (eigen?.inwoners) setInwoners(String(eigen.inwoners));
      } catch { /* geen cijfer voor deze naam gevonden, laat het huidige veld ongewijzigd */ }
    } catch(e) {
      setBevolkingError(e.message);
    }
    setBevolkingLoading(false);
  };

  const updateWijk = (idx, field, val) =>
    setWijken(ws => ws.map((w, i) => i === idx ? { ...w, [field]: val } : w));

  const toggleWijkActief = (idx) =>
    setWijken(ws => ws.map((w, i) => i === idx ? { ...w, actief: !w.actief } : w));

  const uitgeschakeld = wijken.filter(w => !w.actief);

  const toggleWijktype = (idx, key) =>
    setWijken(ws => ws.map((w, i) => {
      if (i !== idx) return w;
      const heeft = w.wijktype.includes(key);
      const nieuw = heeft ? w.wijktype.filter(t => t !== key) : [...w.wijktype, key];
      return { ...w, wijktype: nieuw.length ? nieuw : ['woonwijk'] }; // nooit helemaal leeg
    }));

  const slaOp = async () => {
    setSaving(true); setError('');
    try {
      const evAandeelOverride = {};
      if (evOverride2030 !== '' && evOverride2030 != null) evAandeelOverride[2030] = parseFloat(evOverride2030) / 100;
      if (evOverride2035 !== '' && evOverride2035 != null) evAandeelOverride[2035] = parseFloat(evOverride2035) / 100;

      const bijgewerkt = {
        ...gemeente,
        naam, inwoners: parseInt(inwoners),
        // Is het aantal berekend, dan blijft staan wat de server erin heeft
        // gezet. Anders zou een verouderd scherm het oude getal terugschrijven.
        voertuigen: berekendUitBron ? gemeente.voertuigen : parseInt(voertuigen),
        // De welvaartsindex zit in geen enkele berekening en heeft sinds
        // 9 september 2026 geen invulveld meer. De bewaarde waarde gaat
        // ongewijzigd mee, zodat de kolom houdt wat erin staat.
        welvaartsindex: gemeente.welvaartsindex,
        privePctBerekend: parseFloat(privePctBerekend) / 100,
        evAandeelOverride: Object.keys(evAandeelOverride).length ? evAandeelOverride : undefined,
        postcodes: postcodes.split(',').map(p => p.trim()).filter(Boolean),
        pvpNormPct: pvpNormPct === '' || pvpNormPct == null ? null : Math.max(0, Math.min(100, parseFloat(pvpNormPct))),
        // Alle wijken blijven bewaard, ook die niet meetellen. Het merkteken
        // gaat mee, zodat een wijk later weer aangezet kan worden.
        wijken,
      };
      await updateGemeente(gemeente.id, bijgewerkt);
      if (onCapex) {
        onCapex({ AC: Math.max(0, parseInt(capexAC) || 0),
                  DC: Math.max(0, parseInt(capexDC) || 0),
                  HPC: Math.max(0, parseInt(capexHPC) || 0) });
      }
      onSave(bijgewerkt);
      onClose();
    } catch(e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const s = {
    overlay:  { position:'fixed', inset:0, background:'rgba(18,22,28,0.45)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 },
    modal:    { width:680, maxHeight:'88vh', background:N.paper, border:`1px solid ${N.line2}`, borderRadius:14, display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 24px 60px rgba(18,22,28,0.24), 0 2px 6px rgba(18,22,28,0.08)', fontFamily:"-apple-system,BlinkMacSystemFont,'Inter','Segoe UI',system-ui,sans-serif" },

    header:   { padding:'17px 24px', borderBottom:`1px solid ${N.line}`, display:'flex', justifyContent:'space-between', alignItems:'center', background:`linear-gradient(180deg,#FDFCFA,${N.paper})` },
    title:    { fontSize:16, fontWeight:700, color:N.ink, letterSpacing:'-0.015em' },
    subtitle: { fontSize:12.5, color:N.ink4, marginTop:2 },
    close:    { width:28, height:28, borderRadius:7, border:`1px solid ${N.line2}`, background:N.paper, color:N.ink3, cursor:'pointer', fontSize:15, display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1, padding:0, fontFamily:'inherit' },

    tabs:     { display:'flex', padding:'0 24px', background:N.head, borderBottom:`1px solid ${N.line}` },
    tab:      (a) => ({ padding:'11px 17px', fontSize:13.5, fontWeight:600, cursor:'pointer', color: a ? N.accent : N.ink3, border:'none', background:'transparent', borderBottom:`2.5px solid ${a ? N.accent : 'transparent'}`, marginBottom:-1, fontFamily:'inherit' }),

    body:     { flex:1, overflowY:'auto', padding:'20px 24px' },
    fld:      { marginBottom:17 },
    lab:      { fontSize:12.5, fontWeight:600, color:N.ink2, marginBottom:6, display:'block' },
    inp:      { width:'100%', background:N.paper, border:`1px solid ${N.line2}`, borderRadius:8, padding:'9px 12px', fontSize:14.5, color:N.ink, fontFamily:'inherit', outline:'none', boxSizing:'border-box', fontVariantNumeric:'tabular-nums' },
    hint:     { fontSize:12, color:N.ink4, marginTop:5, lineHeight:1.5 },
    inpVast:  { width:'100%', padding:'9px 11px', border:`1px solid ${N.line}`, borderRadius:8,
                fontSize:14, fontFamily:'inherit', background:N.canvas, color:N.ink3, cursor:'not-allowed' },
    vloot:    { fontSize:12.5, color:N.warn, background:N.warnBg, border:`1px solid ${N.warnBd}`,
                borderRadius:8, padding:'10px 12px', marginBottom:9, lineHeight:1.55 },
    g2:       { display:'grid', gridTemplateColumns:'1fr 1fr', gap:13 },

    // Blok met een knop die gegevens ophaalt bij een externe bron
    abox:     { background:'#F2F7F8', border:'1px solid #D3E3E6', borderRadius:10, padding:'14px 16px', marginBottom:17 },
    abtn:     (uit) => ({ background: uit ? N.line2 : N.accent, border:'none', borderRadius:8, padding:'9px 15px', fontSize:13, fontWeight:700, color:'#fff', cursor: uit ? 'not-allowed' : 'pointer', fontFamily:'inherit', display:'inline-flex', alignItems:'center', gap:7, whiteSpace:'nowrap' }),
    arow:     { display:'flex', gap:13, alignItems:'flex-end' },
    aresult:  { fontSize:12.5, color:N.ink2, lineHeight:1.65, marginTop:11, paddingTop:11, borderTop:'1px solid #D3E3E6' },
    readout:  (gevuld) => ({ border:'1px solid #C5D9DD', background:N.paper, borderRadius:8, padding:'9px 12px', fontSize:16, fontWeight:700, color: gevuld ? N.accent : N.ink4, textAlign:'center', fontVariantNumeric:'tabular-nums', minWidth:100 }),
    err:      { fontSize:12.5, color:N.red, fontWeight:600, marginTop:8 },

    // Wijkkaart
    wCard:    { background:N.paper2, border:`1px solid ${N.line2}`, borderRadius:10, padding:'15px 17px', marginBottom:12 },
    wName:    { fontSize:14, fontWeight:700, color:N.ink, marginBottom:13, letterSpacing:'-0.01em', display:'flex', alignItems:'center', justifyContent:'space-between' },
    wId:      { fontSize:11, fontWeight:600, color:N.ink4, background:N.head, border:`1px solid ${N.line}`, padding:'2px 8px', borderRadius:5 },
    smi:      { width:'100%', background:N.paper, border:`1px solid ${N.line2}`, borderRadius:7, padding:'7px 10px', fontSize:13.5, color:N.ink, fontFamily:'inherit', outline:'none', boxSizing:'border-box', fontVariantNumeric:'tabular-nums' },
    lab2:     { fontSize:11.5, fontWeight:600, color:N.ink3, marginBottom:5, display:'block', lineHeight:1.45 },
    tvak:     (a) => ({ padding:'6px 13px', borderRadius:7, fontSize:12.5, fontWeight:600, cursor:'pointer', border:`1px solid ${a ? N.accent : N.line2}`, background: a ? '#EAF2F3' : N.paper, color: a ? N.accent : N.ink3, userSelect:'none' }),
    trow:     { display:'flex', flexWrap:'wrap', gap:7, marginBottom:14 },
    proxy:    { fontSize:11, fontWeight:600, color:N.warn, background:N.warnBg, border:`1px solid ${N.warnBd}`, padding:'3px 8px', borderRadius:5, whiteSpace:'nowrap' },

    footer:   { padding:'14px 24px', borderTop:`1px solid ${N.line}`, display:'flex', justifyContent:'space-between', alignItems:'center', background:N.head },
    bs:       { background:N.paper, border:`1px solid ${N.line2}`, borderRadius:8, padding:'9px 17px', fontSize:13.5, fontWeight:600, color:N.ink2, cursor:'pointer', fontFamily:'inherit' },
    bp:       (uit) => ({ background: uit ? N.line2 : N.accent, border:'none', borderRadius:8, padding:'9px 19px', fontSize:13.5, fontWeight:700, color:'#fff', cursor: uit ? 'not-allowed' : 'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:7 }),
  };

  const Ververs = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
      <path d="M21 12a9 9 0 11-3.5-7.1"/><path d="M21 3v6h-6"/>
    </svg>
  );

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>

        <div style={s.header}>
          <div>
            <div style={s.title}>{gemeente.naam} bewerken</div>
            <div style={s.subtitle}>Wijzigingen gelden voor de hele prognose</div>
          </div>
          <button style={s.close} onClick={onClose}>✕</button>
        </div>

        <div style={s.tabs}>
          <button style={s.tab(activeTab==='gemeente')} onClick={() => setActiveTab('gemeente')}>Gemeente</button>
          <button style={s.tab(activeTab==='wijken')}   onClick={() => setActiveTab('wijken')}>Wijken</button>
          <button style={s.tab(activeTab==='capex')}    onClick={() => setActiveTab('capex')}>Capex laadinfra</button>
          <button style={s.tab(activeTab==='gis')}      onClick={() => setActiveTab('gis')}>GIS netbeheerder</button>
        </div>

        <div style={s.body}>

          {activeTab === 'gemeente' && (
            <div>
              <div style={s.fld}>
                <label style={s.lab}>Naam</label>
                <input style={s.inp} value={naam} onChange={e => setNaam(e.target.value)} />
              </div>

              <div style={s.g2}>
                <div style={s.fld}>
                  <label style={s.lab}>Inwoners</label>
                  <input style={s.inp} type="number" value={inwoners} onChange={e => setInwoners(e.target.value)} />
                  <div style={s.hint}>Bron: Statbel en Rijksregister</div>
                </div>
                <div style={s.fld}>
                  <label style={s.lab}>Voertuigen</label>
                  <input style={berekendUitBron ? s.inpVast : s.inp} type="number" value={voertuigen}
                         readOnly={berekendUitBron}
                         onChange={e => { if (!berekendUitBron) setVoertuigen(e.target.value); }} />
                  <div style={s.hint}>
                    {berekendUitBron
                      // Tekst van HL, 28 oktober 2026. De factor staat in geo.js
                      // (VOERTUIGEN_PER_HUISHOUDAUTO); verandert hij, dan ook hier.
                      ? 'Het aantal auto\u2019s gekoppeld aan huishoudens in de gemeente volgens Statbel, '
                        + 'verhoogd met een factor 1,1007. Deze factor verdeelt het aantal personenvoertuigen '
                        + 'dat administratief nergens aan gekoppeld is. De auto\u2019s zijn er echter wel. '
                        + 'De LaadAtlas verdeelt ze naar verhouding over alle gemeenten.'
                      : 'Met de hand ingevuld. Voor deze gemeente is het huishoudbestand van Statbel niet beschikbaar.'}
                  </div>
                </div>
              </div>

              <div style={s.abox}>
                <button style={s.abtn(bevolkingLoading)} onClick={verversBevolking} disabled={bevolkingLoading}>
                  <Ververs />
                  {bevolkingLoading ? 'Bezig…' : 'Ververs bevolkingscijfers'}
                </button>
                {bevolkingError && <div style={s.err}>{bevolkingError}</div>}
                {bevolkingResultaat?.eigen && (
                  <div style={s.aresult}>
                    {bevolkingResultaat.gewijzigd
                      ? <>Inwonertal van {bevolkingResultaat.naam} bijgewerkt vanuit het Rijksregister:{' '}
                          {heelGetal(bevolkingResultaat.oud)} werd{' '}
                          <strong style={{color:N.accent}}>{heelGetal(bevolkingResultaat.nieuw)}</strong>.</>
                      : <>Inwonertal van {bevolkingResultaat.naam} gecontroleerd bij het Rijksregister:
                          ongewijzigd, <strong style={{color:N.accent}}>{heelGetal(bevolkingResultaat.nieuw)}</strong>.</>}
                  </div>
                )}
                {bevolkingResultaat && !bevolkingResultaat.eigen && (
                  <div style={s.aresult}>
                    <strong style={{color:N.accent}}>{bevolkingResultaat.bijgewerkt.toLocaleString('nl-BE')}</strong> gemeenten
                    landelijk bijgewerkt vanuit het Rijksregister, waarvan{' '}
                    <strong style={{color:N.accent}}>{bevolkingResultaat.gemeentenBijgewerkt.toLocaleString('nl-BE')}</strong> onboarde
                    gemeenten in de database meteen mee geactualiseerd. Het inwonersveld hierboven
                    toont automatisch het nieuwste cijfer voor {gemeente.naam}.
                  </div>
                )}
              </div>

              <div style={s.fld}>
                <label style={s.lab}>EV-aandeel handmatig overschrijven</label>
                {vlootMelding && (
                  <div style={s.vloot}>
                    In {gemeente.naam} staan veel bedrijfsauto’s ingeschreven, vooral van
                    leasemaatschappijen. Daardoor ligt het aandeel elektrische auto’s in de
                    gemeentelijke statistiek veel hoger dan bij de huishoudens. Dat cijfer hoort
                    hier dus niet.
                  </div>
                )}
                {vlootOnbekend && (
                  <div style={s.hint}>
                    De verhouding tussen ingeschreven auto’s en auto’s van gezinnen is voor deze
                    gemeente niet bekend, want een van beide bronbestanden is niet ingelezen.
                  </div>
                )}
                <div style={s.g2}>
                  <div>
                    <input style={s.inp} type="number" step="0.1" placeholder="2030" value={evOverride2030} onChange={e => setEvOverride2030(e.target.value)} />
                    <div style={s.hint}>2030, in procent</div>
                  </div>
                  <div>
                    <input style={s.inp} type="number" step="0.1" placeholder="2035" value={evOverride2035} onChange={e => setEvOverride2035(e.target.value)} />
                    <div style={s.hint}>2035, in procent</div>
                  </div>
                </div>
                <div style={s.hint}>
                  Alleen invullen als de gemeente een eigen verwachting heeft voor het aandeel
                  elektrische auto’s bij de huishoudens. Blijven de velden leeg, dan rekent het
                  model met de Vlaamse verwachting: {kommagetal(EV_AANDEEL_VLAANDEREN[2030] * 100)} procent
                  elektrisch in 2030 en {kommagetal(EV_AANDEEL_VLAANDEREN[2035] * 100)} procent in 2035.
                </div>
              </div>
              <div style={s.fld}>
                <label style={s.lab}>Postcodes</label>
                <input style={s.inp} value={postcodes} onChange={e => setPostcodes(e.target.value)} placeholder="bijvoorbeeld 3000, 3001, 3010" />
                <div style={s.hint}>Komma-gescheiden. Nodig voor de koppeling met Fluvius.</div>
              </div>


              <div style={s.abox}>
                <div style={s.arow}>
                  <button
                    style={{ ...s.abtn(fluviusLoading || !postcodes.trim()), flex:'1 1 auto', justifyContent:'center' }}
                    onClick={verversVanuitFluvius}
                    disabled={fluviusLoading || !postcodes.trim()}
                  >
                    <Ververs />
                    {fluviusLoading ? 'Bezig…' : 'Haal actuele private laadpunten op bij Fluvius'}
                  </button>
                  <div>
                    <label style={{ ...s.lab, fontSize:11.5, marginBottom:4 }}>EV-rijders met eigen laadpunt</label>
                    <div style={s.readout(fluviusResultaat && fluviusResultaat.huidigeEvs > 0)}>
                      {fluviusResultaat && fluviusResultaat.huidigeEvs > 0
                        ? `${Math.round(Math.min(1, (fluviusResultaat.totaalPrivePunten * FLUVIUS_KAPPA) / fluviusResultaat.huidigeEvs) * 100)}%`
                        : (fluviusLoading ? '…' : '—')}
                    </div>
                  </div>
                </div>
                {fluviusError && <div style={s.err}>{fluviusError}</div>}
                {fluviusResultaat && (
                  <div style={s.aresult}>
                    <strong style={{color:N.accent}}>{fluviusResultaat.totaalPrivePunten.toLocaleString('nl-BE')}</strong> aangemelde
                    private laadpunten bij Fluvius, {Object.keys(fluviusResultaat.perPostcode).length} van {fluviusResultaat.postcodes.length} postcodes
                    bereikt. Ongeveer 35 procent van de eigenaars meldt de thuislader niet aan, dus tellen we die erbij op:{' '}
                    <strong style={{color:N.accent}}>~{Math.round(fluviusResultaat.totaalPrivePunten * FLUVIUS_KAPPA).toLocaleString('nl-BE')}</strong> werkelijke
                    laadpunten, peildatum {HUIDIG_JAAR}.
                    {fluviusResultaat.mislukt?.length > 0 && (
                      <div style={{ color:N.warn, marginTop:6 }}>Niet bereikbaar: {fluviusResultaat.mislukt.join(', ')}</div>
                    )}
                    <div style={{ color:N.ink4, marginTop:8, fontSize:12 }}>
                      Let op: dit percentage is de installatiegraad onder EV-rijders. Dat is iets
                      anders dan de schuifbalk op het tabblad Gemeente, die aangeeft welk deel
                      van álle laadpunten privé is.
                    </div>
                  </div>
                )}
              </div>

            </div>
          )}

          {/* De netgegevens van Fluvius. Inlezen, vervangen en verwijderen gaan
              meteen naar de server, los van de knop Opslaan onderaan. */}
          {activeTab === 'gis' && <GisNetbeheerder gemeenteId={gemeente.id} onGewijzigd={onGisGewijzigd} />}

          {activeTab === 'capex' && (
            <div>
              <div style={s.fld}>
                <label style={s.lab}>Investering per gewoon laadpunt</label>
                <input style={s.inp} type="number" min={0} step={100} value={capexAC}
                       onChange={e => setCapexAC(e.target.value)} />
                <div style={s.hint}>In euro, per laadpunt, inclusief aansluiting en plaatsing.</div>
              </div>
              <div style={s.fld}>
                <label style={s.lab}>Investering per snellader van twee laadpunten</label>
                <input style={s.inp} type="number" min={0} step={500} value={capexDC}
                       onChange={e => setCapexDC(e.target.value)} />
                <div style={s.hint}>In euro, per laadpaal.</div>
              </div>
              <div style={s.fld}>
                <label style={s.lab}>Investering per ultrasnellader van twee laadpunten</label>
                <input style={s.inp} type="number" min={0} step={1000} value={capexHPC}
                       onChange={e => setCapexHPC(e.target.value)} />
                <div style={s.hint}>In euro, per laadpaal.</div>
              </div>
              <div style={s.hint}>
                Deze bedragen gelden alleen voor deze gemeente en bepalen de investering
                op het Dashboard, in de tabellen en in het rapport.
              </div>
            </div>
          )}

          {activeTab === 'wijken' && (
            <div>
              <div style={{ fontSize:12.5, color:N.ink3, marginBottom:12, lineHeight:1.6 }}>
                Een wijk die niet meetelt, blijft in deze lijst staan maar telt niet
                mee in het rekenmodel, op de kaart en in de uitvoer. Het vinkje is op
                elk moment weer aan te zetten.
                {uitgeschakeld.length > 0 && (
                  <span style={{ color:N.ink2, fontWeight:600 }}>
                    {' '}Nu uitgeschakeld: {uitgeschakeld.map(w => w.naam).join(', ')}.
                  </span>
                )}
              </div>
              {wijken.map((wijk, idx) => (
                <div key={wijk.id} style={{ ...s.wCard, opacity: wijk.actief ? 1 : 0.5 }}>
                  <div style={{ ...s.wName, display:'flex', alignItems:'center',
                                justifyContent:'space-between', gap:12 }}>
                    <span>
                      {wijk.naam}
                      <span style={s.wId}>{wijk.id}</span>
                    </span>
                    <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12.5,
                                    fontWeight:600, color: wijk.actief ? N.ink2 : N.ink4,
                                    cursor:'pointer', whiteSpace:'nowrap' }}>
                      <input type="checkbox" checked={wijk.actief}
                        onChange={() => toggleWijkActief(idx)}
                        style={{ width:15, height:15, cursor:'pointer', accentColor:N.accent }} />
                      Meetellen
                    </label>
                  </div>

                  <div style={{ ...s.g2, marginBottom:14 }}>
                    <div>
                      <label style={s.lab2}>Inwoners</label>
                      <input style={s.smi} type="number" value={wijk.inwoners}
                        onChange={e => updateWijk(idx, 'inwoners', parseInt(e.target.value))} />
                    </div>
                    <div>
                      <label style={s.lab2}>Voertuigen</label>
                      <input style={s.smi} type="number" value={wijk.voertuigen}
                        onChange={e => updateWijk(idx, 'voertuigen', parseInt(e.target.value))} />
                    </div>
                  </div>

                  <label style={s.lab2}>
                    Wijktype · bepaalt de doelgroepenmix, meerdere aanvinken voor een gemengde wijk
                  </label>
                  <div style={s.trow}>
                    {WIJKTYPES.map(t => (
                      <div key={t.key} style={s.tvak(wijk.wijktype.includes(t.key))}
                        onClick={() => toggleWijktype(idx, t.key)}>{t.label}</div>
                    ))}
                  </div>

                  <div style={s.g2}>
                    <div>
                      <label style={s.lab2}>Oppervlakte in km²</label>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <input style={{ ...s.smi, flex:1 }} type="number" min="0" step="0.01"
                          value={wijk.oppervlakteKm2 ?? ''}
                          onChange={e => {
                            updateWijk(idx, 'oppervlakteKm2', e.target.value === '' ? null : +e.target.value);
                            updateWijk(idx, 'oppervlakteIsProxy', false);
                          }} />
                        {wijk.oppervlakteIsProxy !== false && <span style={s.proxy}>schatting</span>}
                      </div>
                      <div style={s.hint}>Voor de dekkingsnorm van 250 meter</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>

        <div style={s.footer}>
          <div style={{ fontSize:12.5, color:N.red, fontWeight:600 }}>{error}</div>
          <div style={{ display:'flex', gap:10 }}>
            <button style={s.bs} onClick={onClose}>Annuleren</button>
            <button style={s.bp(saving)} onClick={slaOp} disabled={saving}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
                <path d="M20 6L9 17l-5-5"/>
              </svg>
              {saving ? 'Opslaan…' : 'Opslaan'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
